import type { Dispatch, RefObject, SetStateAction } from "react";
import { Trash2 } from "lucide-react";

import { Button } from "../../ui/button.js";
import { useConfirm } from "../../ui/confirm-dialog.js";
import { disabledInp, inp, Field, type FieldAccess, FieldGrid, CollapsibleSection } from "../sectionShared.js";

type InputProps = { value: string; onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void; disabled?: boolean; style: React.CSSProperties };

export interface EducationEntry { degree: string; institution: string; fieldOfStudy: string; startYear: string; endYear: string }
export interface WorkHistoryEntry { company: string; title: string; startDate: string; endDate: string; isCurrent: boolean }

interface Props {
  sectionRef: RefObject<HTMLDivElement>;
  inputProps: (field: string, value: string, onChange: (v: string) => void) => InputProps;
  isCreate: boolean;
  permissions: Record<string, FieldAccess>;
  skills: string; setSkills: (v: string) => void;
  education: EducationEntry[]; setEducation: Dispatch<SetStateAction<EducationEntry[]>>;
  workHistory: WorkHistoryEntry[]; setWorkHistory: Dispatch<SetStateAction<WorkHistoryEntry[]>>;
}

/**
 * Removes one repeatable row. Rendered as the row's last grid cell, next to the
 * final field, so it stays with its row when `cols="compact"` wraps onto a
 * second line -- the same place the Work History "Current" checkbox sits.
 */
function RemoveRowButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="shrink-0 text-surface-400 hover:text-error"
    >
      <Trash2 size={14} />
    </Button>
  );
}

export function SkillsHistorySection({
  sectionRef, inputProps, isCreate, permissions, skills, setSkills, education, setEducation, workHistory, setWorkHistory,
}: Props) {
  const eduEditable = !isCreate ? permissions["education"]?.canEdit === "DIRECT" : true;
  const eduStyle = eduEditable ? inp : disabledInp;
  const workEditable = !isCreate ? permissions["workHistory"]?.canEdit === "DIRECT" : true;
  const workStyle = workEditable ? inp : disabledInp;
  const confirm = useConfirm();

  // A blank row is scaffolding — drop it without ceremony. A row with anything
  // typed into it is work the user would not expect a single click to discard.
  async function removeEducation(i: number) {
    const row = education[i];
    const hasData = Boolean(
      row && (row.degree || row.institution || row.fieldOfStudy || row.startYear || row.endYear),
    );
    if (hasData) {
      const ok = await confirm({
        title: "Remove this education entry?",
        description: row?.degree
          ? `"${row.degree}"${row.institution ? ` — ${row.institution}` : ""} will be removed from the form.`
          : "This entry will be removed from the form.",
        actionText: "Remove",
        tone: "destructive",
      });
      if (!ok) return;
    }
    setEducation((p) => p.filter((_, j) => j !== i));
  }

  async function removeWorkHistory(i: number) {
    const row = workHistory[i];
    const hasData = Boolean(row && (row.company || row.title || row.startDate || row.endDate));
    if (hasData) {
      const ok = await confirm({
        title: "Remove this work history entry?",
        description: row?.company
          ? `"${row.company}"${row.title ? ` — ${row.title}` : ""} will be removed from the form.`
          : "This entry will be removed from the form.",
        actionText: "Remove",
        tone: "destructive",
      });
      if (!ok) return;
    }
    setWorkHistory((p) => p.filter((_, j) => j !== i));
  }

  return (
    <CollapsibleSection id="skills" sectionRef={sectionRef} title="Skills, Education & Work History">

      <Field label="Skills (comma-separated)">
        {(() => {
          const skillsProps = inputProps("skills", skills, setSkills);
          return (
            <textarea
              value={skillsProps.value}
              onChange={e => { if (!skillsProps.disabled) setSkills(e.target.value); e.target.style.height = "auto"; e.target.style.height = `${e.target.scrollHeight}px`; }}
              onFocus={e => { e.target.style.height = "auto"; e.target.style.height = `${e.target.scrollHeight}px`; }}
              disabled={skillsProps.disabled}
              placeholder="e.g. Python, React, SQL"
              rows={1}
              style={{ ...skillsProps.style, resize: "none", overflow: "hidden", lineHeight: "1.5", paddingTop: "7px", paddingBottom: "7px" }}
            />
          );
        })()}
      </Field>

      {/* Education */}
      <div style={{ marginTop: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
          <p style={{ margin: 0, fontSize: "13px", fontWeight: 700 }}>Education</p>
          {eduEditable && (
            <button
              onClick={() => setEducation((p) => [...p, { degree: "", institution: "", fieldOfStudy: "", startYear: "", endYear: "" }])}
              style={{ background: "none", border: "1px solid var(--color-brand-400)", borderRadius: "var(--radius-sm)", color: "var(--color-brand-600)", padding: "3px 10px", cursor: "pointer", fontSize: "12px" }}
            >
              + Add
            </button>
          )}
        </div>
        {education.map((e, i) => (
          <FieldGrid key={i} cols="compact" gap="sm" className="mb-2.5">
            <Field label="Degree">
              <input disabled={!eduEditable} style={eduStyle} value={e.degree} onChange={(ev) => setEducation((p) => p.map((x, j) => j === i ? { ...x, degree: ev.target.value } : x))} placeholder="B.Tech, MBA…" />
            </Field>
            <Field label="Institution">
              <input disabled={!eduEditable} style={eduStyle} value={e.institution} onChange={(ev) => setEducation((p) => p.map((x, j) => j === i ? { ...x, institution: ev.target.value } : x))} />
            </Field>
            <Field label="Field of Study">
              <input disabled={!eduEditable} style={eduStyle} value={e.fieldOfStudy} onChange={(ev) => setEducation((p) => p.map((x, j) => j === i ? { ...x, fieldOfStudy: ev.target.value } : x))} />
            </Field>
            <Field label="Start Year">
              <input disabled={!eduEditable} style={eduStyle} type="number" value={e.startYear} onChange={(ev) => setEducation((p) => p.map((x, j) => j === i ? { ...x, startYear: ev.target.value } : x))} />
            </Field>
            <Field label="End Year">
              <input disabled={!eduEditable} style={eduStyle} type="number" value={e.endYear} onChange={(ev) => setEducation((p) => p.map((x, j) => j === i ? { ...x, endYear: ev.target.value } : x))} />
            </Field>
            {eduEditable && (
              <div className="flex items-end justify-start">
                <RemoveRowButton label="Remove this education entry" onClick={() => void removeEducation(i)} />
              </div>
            )}
          </FieldGrid>
        ))}
      </div>

      {/* Work History */}
      <div style={{ marginTop: "20px", paddingTop: "16px", borderTop: "1px solid var(--color-surface-100)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
          <p style={{ margin: 0, fontSize: "13px", fontWeight: 700 }}>Work History</p>
          {workEditable && (
            <button
              onClick={() => setWorkHistory((p) => [...p, { company: "", title: "", startDate: "", endDate: "", isCurrent: false }])}
              style={{ background: "none", border: "1px solid var(--color-brand-400)", borderRadius: "var(--radius-sm)", color: "var(--color-brand-600)", padding: "3px 10px", cursor: "pointer", fontSize: "12px" }}
            >
              + Add
            </button>
          )}
        </div>
        {workHistory.map((w, i) => (
          <FieldGrid key={i} cols="compact" gap="sm" className="mb-2.5 items-end">
            <Field label="Company">
              <input disabled={!workEditable} style={workStyle} value={w.company} onChange={(ev) => setWorkHistory((p) => p.map((x, j) => j === i ? { ...x, company: ev.target.value } : x))} />
            </Field>
            <Field label="Job Title">
              <input disabled={!workEditable} style={workStyle} value={w.title} onChange={(ev) => setWorkHistory((p) => p.map((x, j) => j === i ? { ...x, title: ev.target.value } : x))} />
            </Field>
            <Field label="Start">
              <input disabled={!workEditable} style={workStyle} type="month" value={w.startDate} onChange={(ev) => setWorkHistory((p) => p.map((x, j) => j === i ? { ...x, startDate: ev.target.value } : x))} />
            </Field>
            {!w.isCurrent && (
              <Field label="End">
                <input disabled={!workEditable} style={workStyle} type="month" value={w.endDate} onChange={(ev) => setWorkHistory((p) => p.map((x, j) => j === i ? { ...x, endDate: ev.target.value } : x))} />
              </Field>
            )}
            <label style={{ fontSize: "12px", display: "flex", alignItems: "center", gap: "4px" }}>
              <input type="checkbox" disabled={!workEditable} checked={w.isCurrent} onChange={(ev) => setWorkHistory((p) => p.map((x, j) => j === i ? { ...x, isCurrent: ev.target.checked } : x))} />
              Current
            </label>
            {workEditable && (
              <RemoveRowButton label="Remove this work history entry" onClick={() => void removeWorkHistory(i)} />
            )}
          </FieldGrid>
        ))}
      </div>
    </CollapsibleSection>
  );
}
